package fr.frux.feature.treedetail.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.CalendarMonth
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_season_legend_current
import fr.frux.feature.treedetail.resources.treedetail_season_legend_in_season
import fr.frux.feature.treedetail.resources.treedetail_season_legend_soon
import org.jetbrains.compose.resources.stringResource

private val monthInitials = listOf("J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D")

/**
 * Bloc calendrier de saison de la fiche arbre (spec Fiche arbre, variantes D) : conteneur
 * `primaryContainer` rayon 28, titre de période + sous-titre d'état, 12 barres et une légende.
 *
 * Une barre n'encode qu'une chose, l'état du mois : neutre 16 dp, bientôt mûr 36 dp, en récolte
 * 56 dp. Le mois courant garde la hauteur et la couleur de son état (sinon « en récolte » disparaît
 * du mois où on regarde) et se repère par le point terracotta et son initiale en gras.
 *
 * N'est rendu par l'appelant que si l'espèce déclare des fenêtres de récolte : sans donnée, pas de
 * frise entièrement neutre.
 */
@Composable
fun SeasonCalendarBlock(
    inSeasonMonths: Set<Int>,
    soonMonths: Set<Int>,
    currentMonth: Int,
    title: String,
    subtitle: String,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier =
            modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(28.dp))
                .background(MaterialTheme.colorScheme.primaryContainer)
                .padding(horizontal = 20.dp, vertical = FruxSpacing.lg),
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Icon(
                imageVector = Icons.Outlined.CalendarMonth,
                contentDescription = null,
                modifier = Modifier.size(28.dp),
                tint = MaterialTheme.colorScheme.primary,
            )
            Column {
                Text(
                    text = title,
                    style = MaterialTheme.typography.headlineMedium,
                    color = MaterialTheme.colorScheme.onPrimaryContainer,
                )
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.secondary,
                )
            }
        }
        Spacer(Modifier.height(FruxSpacing.md))
        MonthBars(inSeasonMonths, soonMonths, currentMonth)
        Spacer(Modifier.height(FruxSpacing.sm))
        SeasonLegend()
    }
}

private val barHeightInSeason = 56.dp
private val barHeightSoon = 36.dp
private val barHeightNeutral = 16.dp

@Composable
private fun MonthBars(
    inSeasonMonths: Set<Int>,
    soonMonths: Set<Int>,
    currentMonth: Int,
) {
    val colors = MaterialTheme.colorScheme
    val fruxColors = MaterialTheme.fruxColors
    // Une phrase unique pour le lecteur d'écran : énumérer 24 cellules serait du bruit.
    Column(
        Modifier.semantics {
            contentDescription = seasonBarsDescription(inSeasonMonths, soonMonths)
        },
    ) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .height(barHeightInSeason)
                    .clearAndSetSemantics { },
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
            verticalAlignment = Alignment.Bottom,
        ) {
            for (month in 1..12) {
                val isCurrent = month == currentMonth
                val (height, color) =
                    monthBarSpec(
                        inSeason = month in inSeasonMonths,
                        soon = month in soonMonths,
                        neutral = colors.primary.copy(alpha = 0.10f),
                        available = fruxColors.statusAvailable,
                        flowering = fruxColors.statusFlowering,
                    )
                Box(
                    modifier =
                        Modifier
                            .weight(1f)
                            .height(height)
                            .clip(RoundedCornerShape(4.dp))
                            .background(color),
                    contentAlignment = Alignment.TopCenter,
                ) {
                    if (isCurrent) {
                        Box(
                            Modifier
                                .padding(top = 6.dp)
                                .size(6.dp)
                                .clip(CircleShape)
                                .background(fruxColors.accent),
                        )
                    }
                }
            }
        }
        Spacer(Modifier.height(6.dp))
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .clearAndSetSemantics { },
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
        ) {
            for (month in 1..12) {
                val isCurrent = month == currentMonth
                val labelColor =
                    when {
                        isCurrent -> colors.primary
                        month in inSeasonMonths -> colors.secondary
                        month in soonMonths -> colors.onTertiaryContainer
                        else -> colors.secondary
                    }
                Text(
                    text = monthInitials[month - 1],
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = if (isCurrent) FontWeight.Bold else FontWeight.SemiBold,
                    color = labelColor,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.weight(1f),
                )
            }
        }
    }
}

/** Hauteur et couleur d'une barre de mois : un seul axe, l'état de saison. */
private fun monthBarSpec(
    inSeason: Boolean,
    soon: Boolean,
    neutral: Color,
    available: Color,
    flowering: Color,
): Pair<Dp, Color> =
    when {
        inSeason -> barHeightInSeason to available
        soon -> barHeightSoon to flowering
        else -> barHeightNeutral to neutral
    }

/** Légende des trois signes des barres, toujours visible : pas de bouton à découvrir. */
@Composable
private fun SeasonLegend() {
    val fruxColors = MaterialTheme.fruxColors
    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(FruxSpacing.md),
        verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
    ) {
        LegendItem(label = stringResource(Res.string.treedetail_season_legend_in_season)) {
            LegendSwatch(fruxColors.statusAvailable, height = 12.dp)
        }
        LegendItem(label = stringResource(Res.string.treedetail_season_legend_soon)) {
            LegendSwatch(fruxColors.statusFlowering, height = 8.dp)
        }
        LegendItem(
            label = stringResource(Res.string.treedetail_season_legend_current),
            swatchAlignment = Alignment.Center,
        ) {
            Box(
                Modifier
                    .size(6.dp)
                    .clip(CircleShape)
                    .background(fruxColors.accent),
            )
        }
    }
}

@Composable
private fun LegendItem(
    label: String,
    swatchAlignment: Alignment = Alignment.BottomCenter,
    swatch: @Composable () -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Box(Modifier.size(12.dp), contentAlignment = swatchAlignment) { swatch() }
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onPrimaryContainer,
        )
    }
}

@Composable
private fun LegendSwatch(
    color: Color,
    height: Dp,
) {
    Box(
        Modifier
            .size(width = 12.dp, height = height)
            .clip(RoundedCornerShape(2.dp))
            .background(color),
    )
}

/**
 * Résume les barres en une phrase française pour l'accessibilité, même logique que la frise
 * historique du design system (`SeasonStrip`, dont ce bloc est le successeur sur la fiche).
 */
private fun seasonBarsDescription(
    inSeasonMonths: Set<Int>,
    soonMonths: Set<Int>,
): String {
    if (inSeasonMonths.isEmpty() && soonMonths.isEmpty()) return "Hors saison toute l'année"
    val parts = mutableListOf<String>()
    if (inSeasonMonths.isNotEmpty()) {
        parts += "En récolte ${monthsRangePhrase(inSeasonMonths)}"
    }
    if (soonMonths.isNotEmpty()) {
        parts += "bientôt mûr ${monthsRangePhrase(soonMonths)}"
    }
    return parts.joinToString(", ").replaceFirstChar { it.uppercase() }
}

/** Construit « en <mois> » ou « de <mois> à <mois> » à partir d'un ensemble de mois non vide. */
private fun monthsRangePhrase(months: Set<Int>): String {
    val start = months.min()
    val end = months.max()
    return if (start == end) {
        "en ${monthNamesLong[start - 1]}"
    } else {
        "de ${monthNamesLong[start - 1]} à ${monthNamesLong[end - 1]}"
    }
}

private val monthNamesLong =
    listOf(
        "janvier",
        "février",
        "mars",
        "avril",
        "mai",
        "juin",
        "juillet",
        "août",
        "septembre",
        "octobre",
        "novembre",
        "décembre",
    )
